import { PUT_CREATE_CLUSTER_TEAM } from 'constants/api'
import axios from 'axios'
import { useSession } from 'contexts/session-context'
import BottomPopUp from 'components/popUps/bottomPopUp'
import textServices from 'services/text-services'
import Input from 'components/input'
import { Label, ErrorInput } from 'theme/theme'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { ErrorMessage } from '@hookform/error-message'
import Button from 'components/button'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import formServices from 'services/form-builder-services'
import { useContext } from 'react'
import { LoaderContext } from 'contexts/loader-context'
import { Cluster, Group } from './groups-reducer'
import { useGroups } from '.'

interface IProps {
  setOpenCreate: (value: boolean) => any
}

export default function CreateGroup({ setOpenCreate }: IProps) {
  const [{ dynamic }, { setNewGroups }] = useGroups()
  const [{ data: session }] = useSession()
  const { setLoad } = useContext(LoaderContext)
  const { register, setValue, watch, handleSubmit, formState: { errors } } = useForm<{ groupName: string, groupFile: File, image: string | ArrayBuffer }>({ resolver: yupResolver(formServices.formGenerateSchema(["groupName", 'groupFile'])) })
  const image = watch('image')

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]

    if (file) {
      setValue('groupFile', file)
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        setValue('image', reader.result)
      }
    }
  }

  const onSubmit = handleSubmit(async (value) => {
    setLoad(true)
    try {
      const headers = { Authorization: `Bearer ${(session as any)._tokenResponse?.idToken}`, 'Ph-Api-Id': PH_API_ID ?? 1 }
      const formData = new FormData()
      formData.append('file', value.groupFile)
      formData.append('input', JSON.stringify({ name: value.groupName, ImageFileName: value.groupFile?.name, ImageContentType: value.groupFile?.type }))
      const { data: { cluster: clusterData, group: groups } } = await axios.put(PUT_CREATE_CLUSTER_TEAM.replace(':teamId', `${dynamic.id}`), formData, { headers })

      const cluster: Cluster = {
        id: clusterData.id,
        createdAt: clusterData.createdAt,
        imageUrl: clusterData.image.bucket,
        name: clusterData.name,
        profileIdOwner: clusterData.profile.id,
        status: clusterData.status,
        statusId: clusterData.statusId,
        secretSantaConfiguration: clusterData.secretSantaConfiguration
      }

      const group: Group[] = groups?.map((item) => {
        return {
          id: item?.id,
          clusterRoleType: item?.clusterRoleType,
          clusterRoleTypeId: item?.clusterRoleTypeId,
          firstName: item?.profile?.firstName,
          points: item?.points,
          profileId: item?.profile?.id,
        } as Group
      })

      dlTrackEvent("form_send", {
        element: 'crear grupo',
        form_name: 'create group',
        form_field: 'button',
        section: 'groups'
      })

      setNewGroups({ cluster, group })
      setOpenCreate(false)
    } catch (err) {
      console.log(err)
    } finally {
      setLoad(false)
    }
  })

  return (
    <BottomPopUp onExit={() => setOpenCreate(false)}>
      <form className="flex-1 flex flex-col pb-8 max-w-lg groups__start__create" onSubmit={onSubmit}>
        <div className="pb-6">
          <h4 className="title medium text-center">{textServices.findByText('groups/start/create/title', { es: 'Crear grupo', en: 'Create group' })}</h4>
          <p className="description medium text-center">{textServices.findByText('groups/start/create/description', { es: 'Agrega un avatar y dale un nombre a tu grupo para crearlo y empezar a invitar a tus amigos para que participen junto a ti.', en: 'Add an avatar and name your group to create it and start inviting your friends to join you.' })}</p>
        </div>
        <div className="pb-32 space-y-3">
          <label className="flex items-center gap-3 input-container">
            <div className={`flex items-center justify-center border-2 border-solid bg-black bg-opacity-20 rounded-full input-image ${(errors?.groupFile?.message && !image) ? 'input-image-error' : ''}`} style={{ aspectRatio: '1/1', width: '85px' }}>
              <input
                type="file"
                className="hidden"
                accept='.png,.jpeg,.jpg'
                onChange={handleUpload}
              />
              {
                image ? (
                  <img src={image as string} alt="" className="w-full h-full object-cover rounded-full" />
                ) : (
                  <svg width="20" height="26" viewBox="0 0 20 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 0H13.4062L20 6.59375V26H0V0ZM2 2V24H18V8H12V2H2ZM14 3.40625V6H16.5938L14 3.40625ZM10 10L14 14H11V19H9V14H6L10 10ZM6 20H14V22H6V20Z" fill="currentColor" />
                  </svg>
                )
              }
            </div>
            <div className='input-description space-y-2'>
              <p className='text medium'>Selecciona una imagen del grupo</p>
              <div className='button-generic text-center'><p>Subir imagen</p></div>
              <ul>
                <li className='text small'>Usa una imagen cuadrada para mejores resultados</li>
                <li className='text small'>Tiene que tener 400x400 px cómo máximo</li>
              </ul>
            </div>
          </label>
          <ErrorInput className='input-error'>
            <ErrorMessage errors={errors} name="groupFile" />
          </ErrorInput>
          <div className="gap-2 flex-1 flex flex-col input-container">
            <Label className="text-start text-2xl font-bold uppercase input-label">
              {textServices.findByText('groups/start/create/label', { es: 'Nombre del grupo', en: 'Name of group' })}
            </Label>
            <Input
              className="text-center saira font-bold text-sm"
              type="text"
              placeholder={textServices.findByText('groups/start/create/placeholder', { es: 'Escoge un nombre', en: 'Choose a name' })}
              {...register('groupName', {
                onBlur: (e) => {
                  dlTrackEvent('form_interaction', {
                    form_name: 'groups',
                    form_field: 'name',
                    form_action: e.target.value ? 'complete' : 'empty',
                    section: 'groups :: create'
                  })
                }
              })}
            />
            <ErrorInput className='input-error'>
              <ErrorMessage errors={errors} name="groupName" />
            </ErrorInput>
          </div>
        </div>
        <Button>{textServices.findByText('groups/start/create/button', { es: 'Finalizar', en: 'Complete' })}</Button>
      </form>
    </BottomPopUp>
  )
}